import { selectedLocationPropTypes } from '@nandosaus/prop-types';
import { truncate } from 'lodash';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { ANALYTICS_EVENTS, ORDER_TYPES } from '@nandosaus/constants';
import { Box, Flex } from '../../grid';
import { LoadingIndicator } from '../../icons/loading-indicator';
import { NoRestaurantResults } from './no-restaurant-results';
import { P } from '../../typography/new';
import { RestaurantMap } from '../../restaurant-map';
import { RestaurantsList } from '../../restaurant-list';
import { Tabs } from '../../tabs';
import { analytics } from '../../../analytics';

const MAP_VIEW = 'map';
const LIST_VIEW = 'list';

const options = [
  {
    value: LIST_VIEW,
    label: 'List View',
  },
  {
    value: MAP_VIEW,
    label: 'Map View',
  },
];

const MAX_RESULTS = 8;

const RestaurantFinder = ({ orderType, findClosestRestaurants, onRestaurantClick, searchLocation }) => {
  const { RestaurantStore } = useRootStore();
  const [tab, selectTab] = useState(LIST_VIEW);
  const closestRestaurants = findClosestRestaurants(searchLocation.coordinates, MAX_RESULTS).map(
    result => result.restaurant
  );
  const restaurantsFound = closestRestaurants.length > 0;
  // While restaurants are still being fetched (or haven't loaded yet) the store
  // is empty, so closestRestaurants is [] — show a loading state rather than the
  // misleading "no restaurants within ...km" message.
  const isLoadingRestaurants = RestaurantStore.loading || !RestaurantStore.loaded;

  const onFinderRestaurantClick = restaurant => {
    analytics.track(ANALYTICS_EVENTS.FINDER_RESTAURANT_SELECTED, {
      restaurantId: restaurant?.id,
    });

    onRestaurantClick(restaurant);
  };

  return (
    <Box flex="1" display="flex" flexDirection="column">
      {restaurantsFound && (
        <Flex flexDirection="row" justifyContent="center" mt="16px">
          <Tabs options={options} activeTab={tab} selectTab={selectTab} />
        </Flex>
      )}
      {restaurantsFound && (
        <P my="20px" textAlign="center" variant="extra-small">
          Restaurants near &lsquo;{truncate(searchLocation.name, { length: 40 })}&rsquo;
        </P>
      )}
      {restaurantsFound ? (
        <>
          {tab === MAP_VIEW && (
            <RestaurantMap
              coordinates={searchLocation.coordinates}
              linkText="Select Restaurant"
              flex={{ md: '1' }}
              height="546px"
              useMinHeight
              onRestaurantClick={onFinderRestaurantClick}
              restaurants={closestRestaurants}
            />
          )}
          {tab === LIST_VIEW && (
            <RestaurantsList
              orderType={orderType}
              coordinates={searchLocation.coordinates}
              onRestaurantClick={onFinderRestaurantClick}
              restaurants={closestRestaurants}
            />
          )}
        </>
      ) : (
        <Box mt="2.5rem" textAlign="center">
          {isLoadingRestaurants ? <LoadingIndicator size="32px" /> : <NoRestaurantResults />}
        </Box>
      )}
    </Box>
  );
};

RestaurantFinder.propTypes = {
  onRestaurantClick: PropTypes.func.isRequired,
  findClosestRestaurants: PropTypes.func.isRequired,
  searchLocation: PropTypes.shape({
    coordinates: selectedLocationPropTypes.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  orderType: PropTypes.oneOf([
    ORDER_TYPES.PICK_UP,
    ORDER_TYPES.DINE_IN,
    ORDER_TYPES.DELIVERY,
    ORDER_TYPES.CATERING,
    ORDER_TYPES.CATERING_DELIVERY,
  ]).isRequired,
};

const ComposedRestaurantFinder = observer(RestaurantFinder);
export { ComposedRestaurantFinder as RestaurantFinder };
